/**
 * /admin/marketplace — tabbed shell for the Professional Marketplace
 * admin console.
 *
 * Wave 5-Late Marketplace ANCHOR (shell). Ships the executive admin
 * chrome for the tab strip so the anchor screen composes cleanly with
 * the migrated Professionals tab. Consultations / Notify / Categories
 * tabs are deferred to Marketplace propagation.
 *
 * Tab strip discipline:
 *   • Segmented container: `--ds-radius-card` (4px), 1px admin border,
 *     `box-shadow: 0 1px 0 admin-border` — matches Section chrome.
 *   • Active tab: `--ds-color-accent-soft` background,
 *     `--ds-color-accent-strong` text, inset border-left accent bar.
 *   • Inactive tab: transparent background, `--ds-color-ink-muted` text.
 *   • URL-driven ?tab= state preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Briefcase, MessageSquare, Tag, Users } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { api } from "@/api/client";
import AdminProfessionalsTab from "./marketplace/AdminProfessionalsTab";
import AdminConsultationsTab from "./marketplace/AdminConsultationsTab";
import AdminNotifyInterestTab from "./marketplace/AdminNotifyInterestTab";
import AdminCategoriesTab from "./marketplace/AdminCategoriesTab";

const TABS = [
  { key: "professionals", label: "Professionals", testid: "admin-tab-professionals", icon: Briefcase },
  { key: "consultations", label: "Consultation Requests", testid: "admin-tab-consultations", icon: MessageSquare },
  { key: "notify", label: "Notify Interest", testid: "admin-tab-notify", icon: Users },
  { key: "categories", label: "Service Categories", testid: "admin-tab-categories", icon: Tag },
];

export default function AdminMarketplacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("tab") || "professionals";
  const validTab = TABS.some((t) => t.key === active) ? active : "professionals";
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/marketplace/categories");
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  function setTab(key) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  }

  return (
    <AdminLayout
      title="Professional Marketplace"
      subtitle="Approvals, consultation leads, notify-me inbox, service categories."
    >
      <div data-testid="admin-marketplace-root">
        <div
          className="mb-3 inline-flex rounded-[var(--ds-radius-card)] overflow-hidden"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-admin-border)",
            boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
          }}
          role="tablist"
        >
          {TABS.map((t, idx) => {
            const Icon = t.icon;
            const on = validTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={on}
                data-testid={t.testid}
                data-active={on ? "true" : "false"}
                onClick={() => setTab(t.key)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold uppercase tracking-[0.06em] transition-colors"
                style={{
                  background: on ? "var(--ds-color-accent-soft)" : "transparent",
                  color: on
                    ? "var(--ds-color-accent-strong)"
                    : "var(--ds-color-ink-muted)",
                  borderLeft:
                    idx === 0 ? "none" : "1px solid var(--ds-color-admin-border)",
                  boxShadow: on
                    ? "inset 0 -2px 0 0 var(--ds-color-accent)"
                    : "none",
                  transitionDuration: "var(--ds-duration-fast)",
                }}
              >
                <Icon
                  className="w-3.5 h-3.5"
                  style={{
                    color: on
                      ? "var(--ds-color-accent-strong)"
                      : "var(--ds-color-ink-subtle)",
                  }}
                />
                {t.label}
              </button>
            );
          })}
        </div>

        {validTab === "professionals" && (
          <AdminProfessionalsTab categories={categories} />
        )}
        {validTab === "consultations" && <AdminConsultationsTab />}
        {validTab === "notify" && (
          <AdminNotifyInterestTab categories={categories} />
        )}
        {validTab === "categories" && (
          <AdminCategoriesTab onChange={loadCategories} />
        )}
      </div>
    </AdminLayout>
  );
}
